import { useRef, useState } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import './SpiceShowcase.css';

// Import local assets
import garamMasalaImg from '../assets/garam masala1.jpg';
import chilliPowderImg from '../assets/chilli powder.jpg';
import turmericImg from '../assets/turmeric1.jpg';
import chaiMasalaImg from '../assets/chai masala.jpg';
import cuminImg from '../assets/jire masala1.jpg';
import cardamomImg from '../assets/velachi powder1.jpg';

const spices = [
  {
    id: 1,
    name: 'Royal Garam Masala',
    hindi: 'शाही गरम मसाला',
    tagline: 'Hand-ground for the perfect aroma',
    desc: 'A family recipe passed down through generations, ground on stone to preserve every essential oil. This signature blend combines over 15 premium spices for a complex, warm aroma.',
    img: garamMasalaImg,
    accent: '#D4700A',
    badge: 'Heritage'
  },
  {
    id: 2,
    name: 'Kashmiri Lal Mirch',
    hindi: 'कश्मीरी लाल मिर्च',
    tagline: 'The vibrant heart of every curry',
    desc: 'Sun-dried and stem-less, providing a deep crimson hue without the overpowering heat. Handpicked from Kashmiri valleys, these chillies are prized for their brilliant color and mild, smoky flavor.',
    img: chilliPowderImg,
    accent: '#C0392B',
    badge: 'Sun-Dried'
  },
  {
    id: 3,
    name: 'Organic Turmeric Powder',
    hindi: 'हल्दी पाउडर',
    tagline: '9% Curcumin for pure healing',
    desc: 'Sourced from the hills of Meghalaya, this turmeric is world-renowned for its medicinal purity. With a high curcumin content of 9%, it offers a vibrant golden glow and powerful healing properties.',
    img: turmericImg,
    accent: '#B8941F',
    badge: '9% Curcumin'
  },
  {
    id: 4,
    name: 'Aromatic Chai Masala',
    hindi: 'चाय मसाला',
    tagline: 'The soul of Indian mornings',
    desc: 'A blend of cardamom, dry ginger, and black pepper that transforms tea into an experience. Carefully balanced to capture the soul-warming essence of traditional Indian street-side tea.',
    img: chaiMasalaImg,
    accent: '#2D5016',
    badge: 'Bestseller'
  },
  {
    id: 5,
    name: 'Stone-Ground Cumin',
    hindi: 'जीरा पाउडर',
    tagline: 'Earthiness in every pinch',
    desc: 'Slowly roasted and stone-pressed to release an earthy aroma that defines Indian tempering. Sourced from sustainable farms, our cumin ensures that the intense, nutty fragrance remains intact.',
    img: cuminImg,
    accent: '#8B5E3C',
    badge: 'Roasted'
  },
  {
    id: 6,
    name: 'Whole Green Cardamom',
    hindi: 'इलायची',
    tagline: 'The queen of spices',
    desc: "Extra-large pods from the Idukki valley, hand-sorted for uniform size and intense fragrance. Known as the 'Queen of Spices', these pods deliver a sweet, floral flavor indispensable for premium dishes.",
    img: cardamomImg,
    accent: '#4A5D23',
    badge: 'Hand-Sorted'
  }
];

const SpiceCard = ({ spice, index }) => {
  const cardRef = useRef(null);
  const [tilt, setTilt] = useState({ x: 0, y: 0 });

  const handleMouseMove = (e) => {
    if (!cardRef.current) return;
    const rect = cardRef.current.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width - 0.5;
    const y = (e.clientY - rect.top) / rect.height - 0.5;
    setTilt({ x: x * 15, y: y * -15 });
  };

  return (
    <motion.div
      ref={cardRef}
      className="spice-card"
      initial={{ opacity: 0, y: 50 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.8, delay: index * 0.1 }}
      onMouseMove={handleMouseMove}
      onMouseLeave={() => setTilt({ x: 0, y: 0 })}
      style={{
        rotateX: tilt.y,
        rotateY: tilt.x,
        transformStyle: 'preserve-3d',
      }}
    >
      <div className="spice-card-inner" style={{ '--card-accent': spice.accent }}>
        <div className="spice-badge">{spice.badge}</div>

        <div className="spice-img-wrap">
          <img src={spice.img} alt={spice.name} className="spice-img" />
          <div className="spice-img-overlay" style={{ background: spice.accent }} />
        </div>

        <div className="spice-info">
          <div className="spice-header-row">
            <span className="spice-hindi">{spice.hindi}</span>
          </div>
          <h3 className="spice-name">{spice.name}</h3>
          <p className="spice-tagline">{spice.tagline}</p>
          <div className="spice-desc-wrap">
            <p className="spice-desc">{spice.desc}</p>
          </div>
        </div>

        <div className="spice-glow" style={{ background: `radial-gradient(circle at center, ${spice.accent}22 0%, transparent 70%)` }} />
      </div>
    </motion.div>
  );
};

const SpiceShowcase = () => {
  const containerRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: containerRef });
  const x = useTransform(scrollYProgress, [0, 1], [0, -100]);

  return (
    <section className="spice-showcase" ref={containerRef}>
      <div className="container">
        <div className="section-header text-center">
          <span className="section-tag">Pure Selection</span>
          <h2 className="section-title">The <span className="gradient-text">Aromatic</span> Cabinet</h2>
          <p className="section-sub">
            Each spice is a journey of flavor, hand-sorted and stone-ground to bring the
            authentic taste of Indian heritage to your modern kitchen.
          </p>
        </div>

        <div className="spice-grid">
          {spices.map((spice, index) => (
            <SpiceCard key={spice.id} spice={spice} index={index} />
          ))}
        </div>
      </div>

      {/* Decorative Spice Background Elements */}
      <div className="spice-deco-elements">
        <motion.div className="deco-bowl bowl-1" style={{ x }} />
        <motion.div className="deco-bowl bowl-2" style={{ x: useTransform(scrollYProgress, [0, 1], [0, 100]) }} />
      </div>
    </section>
  );
};

export default SpiceShowcase;
